import { useState } from 'react';
import { Zap, Plus, Split, CheckCircle2, Droplet, Flame, Wifi, Trash2, Shield } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { money, date, humanise } from '@/lib/format';
import {
  Badge, Button, Card, ConfirmDialog, EmptyState, Field, Input, LoadingBlock, Modal,
  MoneyInput, PageHeader, Select, Stat, Table, TableWrap, Td, Textarea, Th, useToast, statusTone,
} from '@/components/ui';
import { FileUploader } from '@/components/Files';
import type { Bill, Property, Utility, BillStatus } from '@shared/types';

const CATEGORIES = [
  { value: 'electricity', label: 'Electricity', icon: Zap },
  { value: 'water', label: 'Water', icon: Droplet },
  { value: 'gas', label: 'Gas', icon: Flame },
  { value: 'internet', label: 'Internet / wifi', icon: Wifi },
  { value: 'refuse', label: 'Refuse collection', icon: Trash2 },
  { value: 'security', label: 'Security', icon: Shield },
  { value: 'cleaning', label: 'Cleaning', icon: Shield },
  { value: 'other', label: 'Other', icon: Zap },
];

export default function Bills() {
  const { currency, can } = useAuth();
  const toast = useToast();

  const [propertyId, setPropertyId] = useState('');
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Bill | null>(null);
  const [recharging, setRecharging] = useState<Bill | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: properties } = useApi<Property[]>('/properties');
  const { data, loading, reload } = useApi<Bill[]>('/bills', {
    property_id: propertyId || undefined,
    status: status || undefined,
  });

  const bills = data ?? [];
  const unpaid = bills.filter((b) => b.status !== 'paid');
  const totals = {
    total: bills.reduce((s, b) => s + b.amount_cents, 0),
    outstanding: unpaid.reduce((s, b) => s + b.amount_cents - b.paid_cents, 0),
  };

  async function markPaid(bill: Bill) {
    setBusy(true);
    try {
      await api.post(`/bills/${bill.id}/pay`, {});
      toast.success('Bill marked as paid');
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update the bill');
    } finally {
      setBusy(false);
    }
  }

  async function recharge() {
    if (!recharging) return;
    setBusy(true);
    try {
      const res = await api.post<{ charges_created: number }>(`/bills/${recharging.id}/recharge`, {});
      toast.success(`Split across ${res.charges_created} tenant${res.charges_created === 1 ? '' : 's'}`);
      setRecharging(null);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not recharge that bill');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Property bills"
        subtitle="Electricity, water, gas, wifi and everything else the property costs to run"
        action={can('staff') && (
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
            Add bill
          </Button>
        )}
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Bills on record" value={String(bills.length)} hint={`${unpaid.length} unpaid`} />
        <Stat label="Total billed" value={money(totals.total, currency)} />
        <Stat
          label="Outstanding to providers"
          value={money(totals.outstanding, currency)}
          tone={totals.outstanding > 0 ? 'warn' : 'ok'}
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} className="w-auto min-w-[180px]">
          <option value="">All properties</option>
          {(properties ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-auto min-w-[150px]">
          <option value="">Any status</option>
          {['unpaid', 'partial', 'paid', 'disputed'].map((s) => (
            <option key={s} value={s}>{humanise(s)}</option>
          ))}
        </Select>
      </div>

      <Card>
        {loading ? (
          <div className="p-5"><LoadingBlock rows={6} /></div>
        ) : bills.length === 0 ? (
          <EmptyState
            icon={<Zap className="h-6 w-6" />}
            title="No bills recorded"
            message="Add your utility bills to track what each property costs to run — and split them back to tenants."
            action={can('staff') && (
              <Button variant="primary" size="sm" onClick={() => setCreating(true)}>Add bill</Button>
            )}
          />
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>Bill</Th>
                  <Th>Property</Th>
                  <Th>Period</Th>
                  <Th>Due</Th>
                  <Th align="right">Amount</Th>
                  <Th>Status</Th>
                  {can('staff') && <Th align="right">Actions</Th>}
                </tr>
              </thead>
              <tbody>
                {bills.map((b) => {
                  const Icon = CATEGORIES.find((c) => c.value === b.category)?.icon ?? Zap;
                  const consumption = b.reading_current && b.reading_previous
                    ? Number(b.reading_current) - Number(b.reading_previous)
                    : null;
                  return (
                    <tr key={b.id} className="row-hover">
                      <Td>
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-raised border border-line flex
                                          items-center justify-center shrink-0 text-muted">
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{humanise(b.category)}</p>
                            <p className="text-xs text-faint truncate">{b.provider ?? '—'}</p>
                          </div>
                        </div>
                      </Td>
                      <Td className="text-muted truncate max-w-[160px]">{b.property_name}</Td>
                      <Td className="text-muted text-xs whitespace-nowrap">
                        {b.period_start ? date(b.period_start) : '—'}
                        {consumption !== null && (
                          <span className="block text-faint">{consumption.toLocaleString()} units</span>
                        )}
                      </Td>
                      <Td className="text-muted whitespace-nowrap">{date(b.due_date)}</Td>
                      <Td align="right" className="font-semibold tnum whitespace-nowrap">
                        {money(b.amount_cents, currency)}
                        {b.paid_cents > 0 && b.paid_cents < b.amount_cents && (
                          <span className="block text-xs text-faint">
                            {money(b.paid_cents, currency)} paid
                          </span>
                        )}
                      </Td>
                      <Td>
                        <Badge tone={statusTone(b.status)} dot>{humanise(b.status)}</Badge>
                        {b.recharged && <Badge tone="accent" className="ml-1.5">Split</Badge>}
                      </Td>
                      {can('staff') && (
                        <Td align="right">
                          <div className="flex gap-1 justify-end">
                            {b.status !== 'paid' && (
                              <Button size="sm" variant="ghost" onClick={() => void markPaid(b)}
                                disabled={busy} title="Mark as paid">
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                            )}
                            {!b.recharged && can('manager') && (
                              <Button size="sm" variant="ghost" onClick={() => setRecharging(b)}
                                title="Split across tenants">
                                <Split className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => setEditing(b)}>Edit</Button>
                          </div>
                        </Td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {(creating || editing) && (
        <BillFormModal
          bill={editing ?? undefined}
          onClose={() => { setCreating(false); setEditing(null); }}
          onDone={() => { setCreating(false); setEditing(null); reload(); }}
        />
      )}

      <ConfirmDialog
        open={!!recharging}
        onClose={() => setRecharging(null)}
        onConfirm={() => void recharge()}
        loading={busy}
        tone="primary"
        title="Split this bill across tenants?"
        confirmLabel="Split it"
        message={
          <>
            Every tenant with a live lease on {recharging?.property_name} gets an equal share of{' '}
            <span className="text-ink font-medium">{money(recharging?.amount_cents ?? 0, currency)}</span>{' '}
            added to their ledger as a utility charge. Any rounding remainder goes to the first
            tenant so the totals reconcile exactly.
            <span className="block mt-2">This can only be done once per bill.</span>
          </>
        }
      />
    </div>
  );
}

function BillFormModal({ bill, onClose, onDone }: {
  bill?: Bill; onClose: () => void; onDone: () => void;
}) {
  const { currency } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const [propertyId, setPropertyId] = useState(bill?.property_id ?? '');
  const [utilityId, setUtilityId] = useState(bill?.utility_id ?? '');
  const [category, setCategory] = useState(bill?.category ?? 'electricity');
  const [provider, setProvider] = useState(bill?.provider ?? '');
  const [accountNumber, setAccountNumber] = useState(bill?.account_number ?? '');
  const [periodStart, setPeriodStart] = useState(bill?.period_start?.slice(0, 10) ?? '');
  const [periodEnd, setPeriodEnd] = useState(bill?.period_end?.slice(0, 10) ?? '');
  const [dueDate, setDueDate] = useState(bill?.due_date?.slice(0, 10) ?? new Date().toISOString().slice(0, 10));
  const [amountCents, setAmountCents] = useState(bill?.amount_cents ?? 0);
  const [paidCents, setPaidCents] = useState(bill?.paid_cents ?? 0);
  const [status, setStatus] = useState<BillStatus>(bill?.status ?? 'unpaid');
  const [readingPrev, setReadingPrev] = useState(bill?.reading_previous ?? '');
  const [readingCurr, setReadingCurr] = useState(bill?.reading_current ?? '');
  const [notes, setNotes] = useState(bill?.notes ?? '');
  const [documentFileId, setDocumentFileId] = useState(bill?.document_file_id ?? '');

  const { data: properties } = useApi<Property[]>('/properties');
  const { data: property } = useApi<{ utilities: Utility[] }>(
    propertyId ? `/properties/${propertyId}` : null,
  );

  // Selecting a configured utility fills in the provider details.
  function pickUtility(id: string) {
    setUtilityId(id);
    const u = property?.utilities.find((x) => x.id === id);
    if (u) {
      setCategory(u.category);
      setProvider(u.provider ?? '');
      setAccountNumber(u.account_number ?? '');
      if (u.fixed_cents > 0 && amountCents === 0) setAmountCents(u.fixed_cents);
    }
  }

  async function submit() {
    if (!propertyId) { toast.error('Choose a property'); return; }
    if (amountCents <= 0) { toast.error('Enter the bill amount'); return; }

    setBusy(true);
    try {
      const payload = {
        property_id: propertyId,
        utility_id: utilityId || null,
        category,
        provider: provider || null,
        account_number: accountNumber || null,
        period_start: periodStart || null,
        period_end: periodEnd || null,
        due_date: dueDate,
        amount_cents: amountCents,
        paid_cents: paidCents,
        status,
        reading_previous: readingPrev || null,
        reading_current: readingCurr || null,
        document_file_id: documentFileId || null,
        notes: notes || null,
      };
      if (bill) await api.patch(`/bills/${bill.id}`, payload);
      else await api.post('/bills', payload);
      toast.success(bill ? 'Bill updated' : 'Bill added');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the bill');
    } finally {
      setBusy(false);
    }
  }

  const isMetered = ['electricity', 'water', 'gas'].includes(category);

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={bill ? 'Edit bill' : 'Add a bill'}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}>Save</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Property" required>
            <Select
              value={propertyId}
              onChange={(e) => { setPropertyId(e.target.value); setUtilityId(''); }}
            >
              <option value="">Choose a property…</option>
              {(properties ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
          <Field label="Utility" hint="Links the bill to a configured service">
            <Select value={utilityId} onChange={(e) => pickUtility(e.target.value)} disabled={!propertyId}>
              <option value="">Not linked</option>
              {(property?.utilities ?? []).map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Category">
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </Select>
          </Field>
          <Field label="Provider">
            <Input value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="Kenya Power" />
          </Field>
          <Field label="Account number">
            <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
          </Field>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Period start">
            <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
          </Field>
          <Field label="Period end">
            <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
          </Field>
          <Field label="Due date" required>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
        </div>

        {isMetered && (
          <div className="grid sm:grid-cols-2 gap-4 p-4 rounded-xl bg-raised border border-line">
            <Field label="Previous reading">
              <Input
                type="number"
                step="0.01"
                value={String(readingPrev)}
                onChange={(e) => setReadingPrev(e.target.value)}
              />
            </Field>
            <Field
              label="Current reading"
              hint={readingPrev && readingCurr
                ? `${(Number(readingCurr) - Number(readingPrev)).toLocaleString()} units consumed`
                : undefined}
            >
              <Input
                type="number"
                step="0.01"
                value={String(readingCurr)}
                onChange={(e) => setReadingCurr(e.target.value)}
              />
            </Field>
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Amount" required>
            <MoneyInput valueCents={amountCents} onChangeCents={setAmountCents} currency={currency} />
          </Field>
          <Field label="Amount paid">
            <MoneyInput valueCents={paidCents} onChangeCents={setPaidCents} currency={currency} />
          </Field>
          <Field label="Status" hint="Set automatically from what is paid">
            <Select value={status} onChange={(e) => setStatus(e.target.value as BillStatus)}>
              {['unpaid', 'partial', 'paid', 'disputed'].map((s) => (
                <option key={s} value={s}>{humanise(s)}</option>
              ))}
            </Select>
          </Field>
        </div>

        {bill && (
          <div>
            <p className="label mb-2">Bill document</p>
            <FileUploader
              multiple={false}
              label="Attach a scan or PDF of the bill"
              onUpload={async (payload) => {
                const file = await api.post<{ drive_file_id: string }>('/files', {
                  ...payload,
                  kind: 'bill_document',
                  property_id: propertyId,
                  bill_id: bill.id,
                });
                setDocumentFileId(file.drive_file_id);
              }}
            />
          </div>
        )}

        <Field label="Notes">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
